import React from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { GlobalContext } from "../../context/GlobalContext";
import {
  getMarketingConsent,
  setMarketingConsent,
  hasAcceptedMarketingConsent,
} from "./trackingConsent";
import {
  setTrackingConfig,
  ensureScriptsLoaded,
  trackPageView,
} from "./adsTracker";

/**
 * Loads public tracking IDs, shows consent UI, fires page views on route change.
 */
export function TrackingProvider({ children }) {
  const { hitAxios } = React.useContext(GlobalContext);
  const location = useLocation();
  const theme = useTheme();
  const [consent, setConsent] = React.useState(() => getMarketingConsent());
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await hitAxios({
          path: "/api/web/get_web_public",
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        });
        if (cancelled) return;
        if (res?.data?.success && res.data.data) {
          setTrackingConfig(res.data.data);
        }
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  React.useEffect(() => {
    const onConsent = (e) => setConsent(e.detail || getMarketingConsent());
    window.addEventListener("beyundai:marketing-consent", onConsent);
    return () =>
      window.removeEventListener("beyundai:marketing-consent", onConsent);
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    // Meta Pixel installs even before Accept (Consent API revoke) so Facebook can detect it.
    ensureScriptsLoaded();
    if (!hasAcceptedMarketingConsent()) return;
    trackPageView(location.pathname + location.search);
  }, [ready, consent, location.pathname, location.search]);

  const accept = () => {
    setMarketingConsent("accepted");
    setConsent("accepted");
    ensureScriptsLoaded(); // grants Meta consent + loads other tags
    trackPageView(location.pathname + location.search);
  };

  const decline = () => {
    setMarketingConsent("declined");
    setConsent("declined");
  };

  const showBanner = ready && consent === "unknown";

  return (
    <>
      {children}
      {showBanner && (
        <Box
          role="dialog"
          aria-label="Cookie consent"
          sx={{
            position: "fixed",
            left: { xs: 12, sm: 24 },
            right: { xs: 12, sm: 24 },
            bottom: { xs: 12, sm: 24 },
            zIndex: 1400,
            maxWidth: 560,
            ml: { sm: "auto" },
            p: 2.25,
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            boxShadow: `0 12px 40px ${alpha("#000", 0.18)}`,
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
            We use cookies
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.75}>
            We use cookies to keep you signed in, remember preferences, and
            understand how the site is used so we can improve your experience.
            By accepting, you agree to our use of cookies. You can change email
            preferences anytime in your account notifications.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="text" onClick={decline}>
              Decline
            </Button>
            <Button size="small" variant="contained" onClick={accept}>
              Accept
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}

export default TrackingProvider;
